/**
 * Zod contract for the Minor Custody Agreement & Consent Screening questionnaire.
 *
 * Validates logical answers extracted from a QuestionnaireResponse (by linkId).
 * Used alongside generic required-field checks for format and attestation rules.
 */
import { z } from 'zod';
import type { Questionnaire, QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import {
  MINOR_CUSTODY_CONSENT_SCREENING_LINK_IDS as LINK_IDS,
  MINOR_CUSTODY_CONSENT_SCREENING_QUESTIONNAIRE_URL,
  MINOR_CUSTODY_CONSENT_SCREENING_TEMPLATE_ID,
  QUESTIONNAIRE_TEMPLATE_IDENTIFIER_SYSTEM,
} from '@unify/fhir-model/constants';
import { isValidContactPhoneValue } from './client-contact.schema';
import type { QuestionnaireFillValidationResult } from '../lib/questionnaireResponseFillValidation';

export { LINK_IDS as MINOR_CUSTODY_CONSENT_SCREENING_LINK_IDS };

export const minorCustodyCustodyAnswerValues = ['yes', 'no'] as const;
export type MinorCustodyCustodyAnswer = (typeof minorCustodyCustodyAnswerValues)[number];

export const minorCustodyGuardianRelationshipValues = [
  'parent',
  'legal-guardian',
  'custodial-parent',
  'non-custodial-parent',
  'foster-parent',
  'stepparent',
  'other',
] as const;
export type MinorCustodyGuardianRelationship = (typeof minorCustodyGuardianRelationshipValues)[number];

const fieldToLinkId: Record<string, string> = {
  hasCustodyAgreement: LINK_IDS.custodyAgreement,
  custodyAgreementAttachment: LINK_IDS.custodyUpload,
  guardianName: LINK_IDS.guardianName,
  relationship: LINK_IDS.relationship,
  relationshipOther: LINK_IDS.relationshipOther,
  guardianPhone: LINK_IDS.guardianPhone,
  guardianEmail: LINK_IDS.guardianEmail,
  authorityConfirmed: LINK_IDS.authorityConfirmed,
};

const itemPathForLinkId = (linkId: string): string => `${LINK_IDS.section}/${linkId}`;

function findResponseItemByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
): QuestionnaireResponseItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return item;
    const nested = findResponseItemByLinkId(item.item, linkId);
    if (nested) return nested;
  }
  return undefined;
}

function answerCodingCode(item: QuestionnaireResponseItem | undefined): string | undefined {
  return item?.answer?.[0]?.valueCoding?.code;
}

function answerString(item: QuestionnaireResponseItem | undefined): string {
  return item?.answer?.[0]?.valueString?.trim() ?? '';
}

function answerBoolean(item: QuestionnaireResponseItem | undefined): boolean | undefined {
  return item?.answer?.[0]?.valueBoolean;
}

function hasAttachmentAnswer(item: QuestionnaireResponseItem | undefined): boolean {
  const attachment = item?.answer?.[0]?.valueAttachment;
  return Boolean(attachment?.url || attachment?.data);
}

/** Raw values extracted from a QuestionnaireResponse before Zod validation. */
export type MinorCustodyConsentScreeningParsedValues = {
  hasCustodyAgreement: string;
  custodyAgreementAttachment: boolean;
  guardianName: string;
  relationship: string;
  relationshipOther: string;
  guardianPhone: string;
  guardianEmail: string;
  authorityConfirmed: boolean;
};

/** Map a QuestionnaireResponse to logical form values for Zod validation. */
export function parseMinorCustodyConsentScreeningFromResponse(
  response: Pick<QuestionnaireResponse, 'item'>,
): MinorCustodyConsentScreeningParsedValues {
  const items = response.item;
  const hasCustodyAgreement = answerCodingCode(findResponseItemByLinkId(items, LINK_IDS.custodyAgreement));
  const relationship = answerCodingCode(findResponseItemByLinkId(items, LINK_IDS.relationship));

  return {
    hasCustodyAgreement: hasCustodyAgreement ?? '',
    custodyAgreementAttachment: hasAttachmentAnswer(findResponseItemByLinkId(items, LINK_IDS.custodyUpload)),
    guardianName: answerString(findResponseItemByLinkId(items, LINK_IDS.guardianName)),
    relationship: relationship ?? '',
    relationshipOther: answerString(findResponseItemByLinkId(items, LINK_IDS.relationshipOther)),
    guardianPhone: answerString(findResponseItemByLinkId(items, LINK_IDS.guardianPhone)),
    guardianEmail: answerString(findResponseItemByLinkId(items, LINK_IDS.guardianEmail)),
    authorityConfirmed: answerBoolean(findResponseItemByLinkId(items, LINK_IDS.authorityConfirmed)) === true,
  };
}

export const minorCustodyConsentScreeningSchema = z
  .object({
    hasCustodyAgreement: z.enum(minorCustodyCustodyAnswerValues, {
      message: 'Select whether a custody agreement affects consent',
    }),
    custodyAgreementAttachment: z.boolean(),
    guardianName: z
      .string()
      .trim()
      .min(1, 'Name of parent/guardian consenting to treatment is required')
      .max(200, 'Name is too long'),
    relationship: z.enum(minorCustodyGuardianRelationshipValues, {
      message: 'Select relationship to client',
    }),
    relationshipOther: z.string(),
    guardianPhone: z
      .string()
      .trim()
      .min(1, 'Parent/guardian phone number is required')
      .refine(isValidContactPhoneValue, 'Enter a valid phone number (10–15 digits)'),
    guardianEmail: z.string(),
    authorityConfirmed: z.literal(true, {
      message: 'You must confirm the consenting parent/guardian has authority to consent',
    }),
  })
  .superRefine((data, ctx) => {
    if (data.hasCustodyAgreement === 'yes' && !data.custodyAgreementAttachment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Upload the full custody agreement',
        path: ['custodyAgreementAttachment'],
      });
    }
    if (data.relationship === 'other' && !data.relationshipOther.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please specify relationship to client',
        path: ['relationshipOther'],
      });
    }
    const email = data.guardianEmail.trim();
    if (email.length > 0 && !z.string().email().safeParse(email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid email address or leave the field empty',
        path: ['guardianEmail'],
      });
    }
  });

export type MinorCustodyConsentScreeningInput = z.infer<typeof minorCustodyConsentScreeningSchema>;

export function isMinorCustodyConsentScreeningQuestionnaire(
  questionnaire: Pick<Questionnaire, 'url' | 'identifier' | 'name'>,
): boolean {
  if (questionnaire.url === MINOR_CUSTODY_CONSENT_SCREENING_QUESTIONNAIRE_URL) return true;
  if (questionnaire.name === MINOR_CUSTODY_CONSENT_SCREENING_TEMPLATE_ID) return true;
  return (questionnaire.identifier ?? []).some(
    (id) =>
      id.system === QUESTIONNAIRE_TEMPLATE_IDENTIFIER_SYSTEM &&
      id.value === MINOR_CUSTODY_CONSENT_SCREENING_TEMPLATE_ID,
  );
}

function zodErrorToValidationResult(
  error: z.ZodError,
  scopeLinkIds?: ReadonlySet<string>,
): QuestionnaireFillValidationResult {
  for (const issue of error.issues) {
    const fieldKey = issue.path[0];
    if (typeof fieldKey !== 'string') continue;
    const linkId = fieldToLinkId[fieldKey];
    if (!linkId) continue;
    if (scopeLinkIds && !scopeLinkIds.has(linkId)) continue;
    return {
      success: false,
      // eslint-disable-next-line no-restricted-syntax -- Zod issue text authored in this schema, not a caught error
      message: issue.message,
      missingLinkIds: [linkId],
      missingItemPath: itemPathForLinkId(linkId),
    };
  }
  return {
    success: false,
    message: 'Please complete all required fields.',
    missingLinkIds: [],
    missingItemPath: itemPathForLinkId(LINK_IDS.guardianName),
  };
}

export type ValidateMinorCustodyConsentScreeningOptions = {
  scopeLinkIds?: ReadonlySet<string>;
};

/**
 * Template-specific Zod validation for minor custody consent screening responses.
 */
export function validateMinorCustodyConsentScreeningResponse(
  response: Pick<QuestionnaireResponse, 'item'>,
  options?: ValidateMinorCustodyConsentScreeningOptions,
): QuestionnaireFillValidationResult {
  const parsed = minorCustodyConsentScreeningSchema.safeParse(
    parseMinorCustodyConsentScreeningFromResponse(response),
  );
  if (parsed.success) {
    return { success: true };
  }
  return zodErrorToValidationResult(parsed.error, options?.scopeLinkIds);
}
